import {
  TimezoneDTO,
  PreferredContactMethodDTO,
  NewUserAndAccountDTO,
  NewRegUserDTO,
  UpdateRegUserDTO,
  UserProfile,
  UserDTO,
  UserRecordDTO,
  CityStateDTO,
  ReturnMessage,
  RegUserAdminStatusDTO,
  RegUserActiveStatusDTO,
  UserInviteDTO,
  IndustryDTO
} from '../types';
import BaseAPI from "./BaseAPI";
import CognitoAPI from './CognitoAPI';
import TypeMapping from '../util/typeMapping';
import { APIError, isErrorMessage } from "../util/Exceptions";

export default class CodUserAPI extends BaseAPI {

  static async createUser(newRecord: NewUserAndAccountDTO | NewRegUserDTO, password: string): Promise<ReturnMessage> {
    try {
      const user_guid = await CognitoAPI.signUp(newRecord.user.email_address, password, {});
      // Create a copy of newRecord with the user_guid added to the user object.
      const updatedRecord = {
        ...newRecord,
        user: {
          ...newRecord.user,
          user_guid: user_guid,
        }
      };

      const path = '/register-user';
      const response = await BaseAPI.callPost(path, updatedRecord);
      
      return response;
    } catch (error) {
      console.error(error);
      if (isErrorMessage(error)) {
        throw new APIError(error); // Propagate the error so the caller can handle it
      } else {
        throw error;
      }
    }

  }


  static async getUser(user_guid: string): Promise<UserRecordDTO> {
    const path = `/user/${user_guid}`;
    try {
      return await BaseAPI.callGET(path) as UserRecordDTO;
    } catch (error) {
      console.error(error);
      if (isErrorMessage(error)) {
        throw new APIError(error); // Propagate the error so the caller can handle it
      } else {
        throw error;
      }
    }
  }

  static async getUsers(account_guid: string, is_active?: boolean): Promise<UserRecordDTO[]> {

    interface QueryParams {
      isactive?: boolean;
    }
    let queryParams: QueryParams = {};
  
    if (is_active !== undefined && is_active !== null) {
      queryParams.isactive = is_active;
    }
    const path = `/users/${account_guid}`;
    try {
      if (Object.keys(queryParams).length === 0) {
        return await BaseAPI.callGET(path);
      } else {
        return await BaseAPI.callGET(path, queryParams);
      }
    } catch (error) {
      console.error(error);
      if (isErrorMessage(error)) {
        throw new APIError(error); // Propagate the error so the caller can handle it
      } else {
        throw error;
      }
    }
  }  

  static async updateUser(request_body: UpdateRegUserDTO): Promise<UserRecordDTO> {
    const path = '/user';
    try {
      return await BaseAPI.callPut(path, request_body) as UserRecordDTO;
    } catch (error) {
      console.error(error);
      if (isErrorMessage(error)) {
        throw new APIError(error); // Propagate the error so the caller can handle it
      } else {
        throw error;
      }
    }
  }

  static async inviteUser(account_guid: string, email_address: string) : Promise<ReturnMessage> {
    const path = '/invite-user';
    try {
      return await BaseAPI.callPost(path, { account_guid, email_address });
    } catch (error) {
      console.error(error);
      if (isErrorMessage(error)) {
        throw new APIError(error); // Propagate the error so the caller can handle it
      } else {
        throw error;
      }
    }
  }

  static async reinviteUser(account_guid: string, email_address: string) : Promise<ReturnMessage> {
    const path = '/reinvite-user';
    try {
      return await BaseAPI.callPost(path, { account_guid, email_address });
    } catch (error) {
      console.error(error);
      if (isErrorMessage(error)) {
        throw new APIError(error); // Propagate the error so the caller can handle it
      } else {
        throw error;
      }
    }
  }

  static async me(user: UserDTO): Promise<UserProfile> {
    try {
      const user_record = await this.getUser(user.user_guid);
      return TypeMapping.mapUserRecordToUserProfile(user_record);
    } catch (error) {
      console.error(error);
      if (isErrorMessage(error)) {
        throw new APIError(error); // Propagate the error so the caller can handle it
      } else {
        throw error;
      }
    }
  }

  static async toggleAdminStatus(request_body: RegUserAdminStatusDTO): Promise<ReturnMessage> {
    const path = '/admin/toggle-admin';
    try {
      return await BaseAPI.callPut(path, request_body) as ReturnMessage;
    } catch (error) {
      console.error(error);
      if (isErrorMessage(error)) {
        throw new APIError(error); // Propagate the error so the caller can handle it
      } else {
        throw error;
      }
    }
  }

  static async toggleActiveStatus(request_body: RegUserActiveStatusDTO): Promise<ReturnMessage> {
    const path = '/admin/toggle-active';
    try {
      return await BaseAPI.callPut(path, request_body) as ReturnMessage;
    } catch (error) {
      console.error(error);
      if (isErrorMessage(error)) {
        throw new APIError(error); // Propagate the error so the caller can handle it
      } else {
        throw error;
      }
    }
  }

  static async get_preferred_contact_methods(): Promise<PreferredContactMethodDTO[]> {
    return (
      [
        {
          id: 'text',
          name: 'Text',
          order: 0,
        },
        {
          id: 'email',
          name: 'Email',
          order: 1,
        }
      ]
    );
  }

static async get_industries(): Promise<IndustryDTO[]> {
    return (
      [
        {
            id: 'insurance',
            name: 'Insurance',
            order: 0,
        },
        {
            id: 'agriculture_and_forestry',
            name: 'Agriculture and Forestry',
            order: 1,
        },
        {
            id: 'automotive',
            name: 'Automotive',
            order: 2,
        },
        {
            id: 'banking_and_financial_services',
            name: 'Banking and Financial Services',
            order: 3,
        },
        {
            id: 'construction_and_real_estate',
            name: 'Construction and Real Estate',
            order: 4,
        },
        {
            id: 'consumer_goods',
            name: 'Consumer Goods',
            order: 5,
        },
        {
            id: 'education',
            name: 'Education',
            order: 6,
        },
        {
            id: 'energy_and_utilities',
            name: 'Energy and Utilities',
            order: 7,
        },
        {
            id: 'entertainment_and_leisure',
            name: 'Entertainment and Leisure',
            order: 8,
        },
        {
            id: 'healthcare_and_pharmaceuticals',
            name: 'Healthcare and Pharmaceuticals',
            order: 9,
        },
        {
            id: 'information_technology_and_telecommunications',
            name: 'Information Technology and Telecommunications',
            order: 10,
        },
        {
            id: 'manufacturing',
            name: 'Manufacturing',
            order: 11,
        },
        {
            id: 'media_and_publishing',
            name: 'Media and Publishing',
            order: 12,
        },
        {
            id: 'non_profit_and_government',
            name: 'Non-Profit and Government',
            order: 13,
        },
        {
            id: 'professional_services',
            name: 'Professional Services',
            order: 14,
        },
        {
            id: 'retail_and_ecommerce',
            name: 'Retail and E-Commerce',
            order: 15,
        },
        {
            id: 'transportation_and_logistics',
            name: 'Transportation and Logistics',
            order: 16,
        },
        {
            id: 'travel_and_hospitality',
            name: 'Travel and Hospitality',
            order: 17,
        },
        {
          id: 'other',
          name: 'Other',
          order: 18,
        },
    ]
  );
}

  static async getCitiesStates(postal_code: string): Promise<CityStateDTO[]> {
    const path = `/cities-states/${postal_code}`;
    try {
      return await BaseAPI.callGET(path);
    } catch (error) {
      console.error(error);
      if (isErrorMessage(error)) {
        throw new APIError(error); // Propagate the error so the caller can handle it
      } else {
        throw error;
      }
    }
  }


  static async get_time_zones(): Promise<TimezoneDTO[]> {
    return (
      // TEMPORARY - Hard coded for now. Do we really need to call an API for this?
      [{
        id: 0,
        name: 'Pacific',
        timeZoneName: 'US/Pacific'
      },]
    );
  }

  static async getInvites(account_guid: string): Promise<UserInviteDTO[]> {
    const path = `/invites/${account_guid}`;
    try {
        return await BaseAPI.callGET(path);
    } catch (error) {
      console.error(error);
      if (isErrorMessage(error)) {
        throw new APIError(error); // Propagate the error so the caller can handle it
      } else {
        throw error;
      }
    }
  }

  static async deleteInvite(account_guid: string, email_address: string): Promise<ReturnMessage> {
    const path = `/user-invite/${account_guid}/${email_address}`;
    try {
      return await BaseAPI.callDelete(path);
    } catch (error) {
      console.error(error);
      if (isErrorMessage(error)) {
        throw new APIError(error); // Propagate the error so the caller can handle it
      } else {
        throw error;
      }
    }
  }

  // static async set_time_zone(token: string, data: BrokerTimeZoneBindingModel) {
  // }

  // static async test_sms(access_token: string): Promise<string> {
  // }

  // static async base_field_tooltips(access_token: string): Promise<CarrierBrokerBaseFieldTooltipDTO[]> {
  // }

  // static async update_reguser_profile(model: BrokerUpdateProfileBindingModel, access_token: string): Promise<BrokerNotificationStatusDTO> {
  // }

  // static async reguser_registration(token: TokenValidation): Promise<BrokerRegistrationDTO> {
  // }

  // static async register_reguser(model: BrokerRegistrationBindingModel) {
  // }

  // static async load_reguser_association(carrier_guid: string, token: string) : Promise<BrokerAssociationPageDTO> {
  // }

}
